import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import { Users as UsersIcon, Store, Code } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://tecteach-server.vercel.app/api/v1';

interface Stats {
  totalUsers: number;
  totalOrganizations: number;
  totalProjects: number;
}

const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ElementType;
  color: string;
}> = ({ title, value, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 3,
      borderRadius: 3,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        transform: 'translateY(-2px)',
      },
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={700}>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={28} color={color} />
      </Box>
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState<Stats>({ totalUsers: 0, totalOrganizations: 0, totalProjects: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [usersRes, orgsRes, projectsRes] = await Promise.all([
          axios.get(`${API_URL}/users?whiteLabelClientId=${admin?.whiteLabelClientId}`),
          axios.get(`${API_URL}/users/get-all-organizations?whiteLabelClientId=${admin?.whiteLabelClientId}`),
          axios.get(`${API_URL}/users/get-all-projects?whiteLabelClientId=${admin?.whiteLabelClientId}`),
        ]);

        setStats({
          totalUsers: usersRes.data.length,
          totalOrganizations: orgsRes.data.length,
          totalProjects: projectsRes.data.length,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (admin?.whiteLabelClientId) {
      fetchStats();
    }
  }, [admin]);

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Typography variant="h4" fontWeight={700} color="#142060" gutterBottom>
          Dashboard Overview
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Welcome back, {admin?.email}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard title="Total Users" value={stats.totalUsers} icon={UsersIcon} color="#6355FF" />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Total Organizations" value={stats.totalOrganizations} icon={Store} color="#9333E9" />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="Total Projects" value={stats.totalProjects} icon={Code} color="#A855F7" />
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default Dashboard;
